import React from 'react';
import cn from 'classnames';
import { Icon, type IconType } from 'admin/components/common/Icon';
import css from './RowAction.scss';

const ICONS = {
	add: { type: 'plus-rounded', width: 16 },
	remove: { type: 'minus-rounded', width: 16 },
	reset: { type: 'sc-rotate-left', width: 14 },
} as const satisfies Record<string, { type: IconType; width: number }>;

type Props = {
	action: keyof typeof ICONS;
	/** Accessible name, e.g. "Remove layer" */
	label: string;
	/** Omit when a wrapper handles the click, e.g. a popover trigger */
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
	/** Placement or visibility in the host row, e.g. shown only while the row is hovered */
	className?: string;
};

/**
 * An icon action on an Inspector row: add, remove or reset. One family of round glyphs with no hover fill; the glyph
 * brightens on hover and gets a ring on keyboard focus.
 */
const RowAction = React.forwardRef<HTMLButtonElement, Props>(function RowActionButton(
	{ action, label, onClick, disabled, className },
	ref
) {
	const icon = ICONS[action];
	return (
		<button
			ref={ref}
			type="button"
			className={cn(css.action, className)}
			aria-label={label}
			disabled={disabled}
			onClick={onClick}
		>
			{/* Icons paint white by default; the glyph follows the button's color through its states */}
			<Icon type={icon.type} width={icon.width} color="currentColor" />
		</button>
	);
});

export default RowAction;
